/* =====================================================================
   Don Barato · facets.js
   Filtros, orden y «Cargar más» sin recargar en colección y búsqueda.
   Marcado (ver sections/main-collection.liquid y main-search.liquid):
     [data-facets][data-section-id][data-url]   raíz de la sección
     [data-facets-target="…"]                   zonas que se reemplazan al filtrar
     form[data-facets-form]                     formulario de filtros (escritorio y cajón)
     select[data-sort]                          orden
     a[data-facet-link]                         quitar un filtro / limpiar
     a[data-load-more]                          siguiente página (se agrega a la grilla)
   Usa la Section Rendering API (?section_id=…) y actualiza la URL.
   ===================================================================== */
(function () {
  const roots = new Map();

  class Facets {
    constructor(root) {
      this.root = root;
      this.id = root.dataset.sectionId;
      this.ctrl = null;
      this.timer = null;
      root.addEventListener('change', (e) => this.onChange(e));
      root.addEventListener('input', (e) => this.onInput(e));
      root.addEventListener('submit', (e) => this.onSubmit(e));
      root.addEventListener('click', (e) => this.onClick(e));
    }

    /* ---------- URL ---------- */
    sortValue() {
      const s = this.root.querySelector('[data-sort]');
      return s ? s.value : '';
    }

    activeForm() {
      return (
        this.root.querySelector('.facets-drawer.is-open [data-facets-form]') ||
        this.root.querySelector('.coll-sidebar [data-facets-form]') ||
        this.root.querySelector('[data-facets-form]')
      );
    }

    paramsFrom(form) {
      const params = new URLSearchParams();
      if (form) {
        for (const [k, v] of new FormData(form)) {
          if (String(v).trim() !== '') params.append(k, v);
        }
      } else {
        new URLSearchParams(window.location.search).forEach((v, k) => params.append(k, v));
      }
      params.delete('page');
      params.delete('section_id');
      const sort = this.sortValue();
      if (sort) params.set('sort_by', sort);
      return params;
    }

    urlFor(params) {
      const base = this.root.dataset.url || window.location.pathname;
      const q = params.toString();
      return q ? base + '?' + q : base;
    }

    /* ---------- Eventos ---------- */
    onChange(e) {
      const t = e.target;
      if (t.matches('[data-sort]')) {
        this.go(this.urlFor(this.paramsFrom(this.activeForm())));
        return;
      }
      const form = t.closest('[data-facets-form]');
      if (!form || t.matches('[data-price-input]')) return; // el precio se aplica al dejar de escribir
      this.go(this.urlFor(this.paramsFrom(form)));
    }

    onInput(e) {
      const t = e.target;
      if (!t.matches('[data-price-input]')) return;
      const form = t.closest('[data-facets-form]');
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.go(this.urlFor(this.paramsFrom(form))), 700);
    }

    onSubmit(e) {
      const form = e.target.closest('[data-facets-form]');
      if (!form) return;
      e.preventDefault();
      clearTimeout(this.timer);
      this.go(this.urlFor(this.paramsFrom(form)));
    }

    onClick(e) {
      const more = e.target.closest('a[data-load-more]');
      if (more) {
        e.preventDefault();
        this.loadMore(more);
        return;
      }
      const link = e.target.closest('a[data-facet-link]');
      if (link) {
        e.preventDefault();
        this.go(link.href);
        return;
      }
      const toggle = e.target.closest('[data-facet-more]');
      if (toggle) {
        const facet = toggle.closest('.facet');
        facet.classList.toggle('is-expanded');
        toggle.textContent = facet.classList.contains('is-expanded') ? 'Ver menos' : toggle.dataset.label;
      }
    }

    /* ---------- Red ---------- */
    async fetchSection(url) {
      if (this.ctrl) this.ctrl.abort();
      this.ctrl = new AbortController();
      const u = new URL(url, window.location.origin);
      u.searchParams.set('section_id', this.id);
      const res = await fetch(u.toString(), { signal: this.ctrl.signal, headers: { Accept: 'text/html' } });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      return new DOMParser().parseFromString(await res.text(), 'text/html');
    }

    async go(url, push = true) {
      const grid = this.root.querySelector('[data-facets-target="grid"]');
      if (grid) grid.classList.add('is-loading');
      this.root.setAttribute('aria-busy', 'true');
      try {
        const doc = await this.fetchSection(url);
        this.swap(doc);
        if (push) {
          const u = new URL(url, window.location.origin);
          u.searchParams.delete('section_id');
          window.history.pushState({ facets: this.id }, '', u.pathname + u.search);
        }
      } catch (err) {
        if (err && err.name === 'AbortError') return;
        window.location.href = url; // si falla, navegación normal
      } finally {
        const g = this.root.querySelector('[data-facets-target="grid"]');
        if (g) g.classList.remove('is-loading');
        this.root.removeAttribute('aria-busy');
      }
    }

    swap(doc) {
      const focusId = document.activeElement && document.activeElement.id;
      const open = new Map();
      this.root.querySelectorAll('[data-facets-target] details[data-facet]').forEach((d) => {
        const zone = d.closest('[data-facets-target]').dataset.facetsTarget;
        open.set(zone + '|' + d.dataset.facet, d.open);
      });
      const expanded = new Set();
      this.root.querySelectorAll('[data-facets-target] .facet.is-expanded').forEach((d) => {
        expanded.add(d.closest('[data-facets-target]').dataset.facetsTarget + '|' + d.dataset.facet);
      });

      this.root.querySelectorAll('[data-facets-target]').forEach((el) => {
        const next = doc.querySelector('[data-facets-target="' + el.dataset.facetsTarget + '"]');
        if (next) el.innerHTML = next.innerHTML;
      });

      this.root.querySelectorAll('[data-facets-target] details[data-facet]').forEach((d) => {
        const key = d.closest('[data-facets-target]').dataset.facetsTarget + '|' + d.dataset.facet;
        if (open.has(key)) d.open = open.get(key);
        if (expanded.has(key)) {
          d.classList.add('is-expanded');
          const t = d.querySelector('[data-facet-more]');
          if (t) t.textContent = 'Ver menos';
        }
      });

      if (focusId) {
        const el = document.getElementById(focusId);
        if (el) el.focus({ preventScroll: true });
      }
    }

    async loadMore(btn) {
      btn.classList.add('is-loading');
      try {
        const doc = await this.fetchSection(btn.href);
        const list = this.root.querySelector('[data-facets-grid]');
        const items = doc.querySelectorAll('[data-facets-grid] > li');
        const first = items[0];
        items.forEach((li) => list.appendChild(document.importNode(li, true)));
        const zone = this.root.querySelector('[data-facets-target="more"]');
        const nextZone = doc.querySelector('[data-facets-target="more"]');
        if (zone && nextZone) zone.innerHTML = nextZone.innerHTML;
        // foco al primer producto nuevo (accesibilidad)
        if (first) {
          const added = list.children[list.children.length - items.length];
          const link = added && added.querySelector('a[href]:not([tabindex="-1"])');
          if (link) link.focus({ preventScroll: true });
        }
      } catch (err) {
        if (!err || err.name !== 'AbortError') window.location.href = btn.href;
      } finally {
        btn.classList.remove('is-loading');
      }
    }
  }

  function init() {
    document.querySelectorAll('[data-facets]').forEach((root) => {
      if (!roots.has(root)) roots.set(root, new Facets(root));
    });
  }

  window.addEventListener('popstate', () => {
    roots.forEach((f, root) => {
      if (document.contains(root)) f.go(window.location.href, false);
    });
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
